import React, { useEffect, useState, createElement, Component } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { points, profiles, pointAnalysis, WHATSAPP_NUMBER, ProfileType } from './data';
type Screen = 'entry' | 'points' | 'results';
export function EstructuraPage() {
  const [currentScreen, setCurrentScreen] = useState<Screen>('entry');
  const [currentPointIndex, setCurrentPointIndex] = useState(0);
  const [answers, setAnswers] = useState<number[]>(new Array(8).fill(-1));
  // Persist state
  useEffect(() => {
    const savedState = sessionStorage.getItem('estructuraState');
    if (savedState) {
      const parsed = JSON.parse(savedState);
      if (parsed.answers) setAnswers(parsed.answers);
      if (parsed.currentScreen) setCurrentScreen(parsed.currentScreen);
      if (parsed.currentPointIndex !== undefined) setCurrentPointIndex(parsed.currentPointIndex);
    }
  }, []);
  useEffect(() => {
    sessionStorage.setItem('estructuraState', JSON.stringify({
      answers,
      currentScreen,
      currentPointIndex
    }));
  }, [answers, currentScreen, currentPointIndex]);
  // Meta tags
  useEffect(() => {
    document.title = 'Checklist de Estructura Operativa — Oscar Rodriguez';
    let metaRobots = document.querySelector('meta[name="robots"]');
    if (!metaRobots) {
      metaRobots = document.createElement('meta');
      metaRobots.setAttribute('name', 'robots');
      document.head.appendChild(metaRobots);
    }
    metaRobots.setAttribute('content', 'noindex, nofollow');
    const setMetaTag = (property: string, content: string) => {
      let tag = document.querySelector(`meta[property="${property}"]`);
      if (!tag) {
        tag = document.createElement('meta');
        tag.setAttribute('property', property);
        document.head.appendChild(tag);
      }
      tag.setAttribute('content', content);
    };
    setMetaTag('og:title', 'Checklist de Estructura Operativa — ¿Qué tan sólida es la estructura de tu negocio?');
    setMetaTag('og:description', '8 puntos para identificar si tu negocio depende de ti — y dónde empezar a construir el sistema.');
  }, []);
  // Redirect if accessing results without completing
  useEffect(() => {
    if (currentScreen === 'results' && answers.includes(-1)) {
      setCurrentScreen('entry');
    }
  }, [currentScreen, answers]);
  const handleAnswer = (value: number) => {
    const newAnswers = [...answers];
    newAnswers[currentPointIndex] = value;
    setAnswers(newAnswers);
  };
  const handleNext = () => {
    if (currentPointIndex < 7) {
      setCurrentPointIndex(prev => prev + 1);
      window.scrollTo(0, 0);
    } else {
      setCurrentScreen('results');
      window.scrollTo(0, 0);
    }
  };
  const handlePrev = () => {
    if (currentPointIndex > 0) {
      setCurrentPointIndex(prev => prev - 1);
      window.scrollTo(0, 0);
    }
  };
  const renderEntry = () => <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0
  }} className="min-h-screen flex flex-col items-center justify-center px-6 text-center py-12" data-id="element-230">
      <img src="https://cdn.magicpatterns.com/uploads/8iq9FdZPWhhg1moFGMfStg/Gemini_Generated_Image_vohteevohteevoht_(1)-Photoroom.png" alt="OR Logo" className="h-16 mb-8" data-id="element-231" />
      <p className="text-gold text-sm font-bold tracking-widest uppercase mb-6" data-id="element-232">
        CHECKLIST
      </p>
      <h1 className="text-4xl md:text-6xl font-serif font-bold text-white mb-6 leading-tight max-w-4xl" data-id="element-233">
        ¿Qué tan sólida es la estructura operativa de tu negocio?
      </h1>
      <h2 className="text-xl md:text-2xl font-serif text-gold mb-8 max-w-2xl leading-relaxed" data-id="element-234">
        8 puntos para identificar si tu negocio depende de ti — y dónde empezar
        a construir el sistema que te permita salir.
      </h2>
      <p className="text-gray-light text-lg max-w-[600px] mb-12 leading-relaxed" data-id="element-235">
        8 preguntas concretas sobre tu operación. Al final, vas a saber qué tan
        sólida es la estructura que sostiene tu negocio hoy y por dónde es
        prioritario intervenir.
      </p>
      <button onClick={() => setCurrentScreen('points')} className="bg-gold hover:bg-yellow-500 text-navy font-bold text-xl px-10 py-5 rounded transition-all duration-300 hover:scale-105 shadow-[0_0_20px_rgba(201,168,76,0.3)]" data-id="element-236">
        Comenzar checklist →
      </button>
      <p className="text-sm text-gray-light/60 mt-6" data-id="element-237">
        Para dueños de negocio con operación activa. Toma 6 minutos.
      </p>
    </motion.div>;
  const renderPoint = () => {
    const point = points[currentPointIndex];
    const progress = (currentPointIndex + 1) / 8 * 100;
    const hasAnswered = answers[currentPointIndex] !== -1;
    return <motion.div key={currentPointIndex} initial={{
      opacity: 0,
      x: 20
    }} animate={{
      opacity: 1,
      x: 0
    }} exit={{
      opacity: 0,
      x: -20
    }} className="min-h-screen flex flex-col px-6 py-12 max-w-3xl mx-auto w-full" data-id="element-238">
        {/* Progress Bar */}
        <div className="w-full mb-16" data-id="element-239">
          <p className="text-xs text-gray-light mb-2 text-right uppercase tracking-wider" data-id="element-240">
            Punto {currentPointIndex + 1} de 8
          </p>
          <div className="w-full h-1 bg-navy-dark rounded-full overflow-hidden" data-id="element-241">
            <div className="h-full bg-gold transition-all duration-500" style={{
            width: `${progress}%`
          }} data-id="element-242" />
          </div>
        </div>

        <div className="flex-grow flex flex-col" data-id="element-243">
          <div className="mb-12" data-id="element-244">
            <span className="block text-7xl md:text-8xl font-serif font-bold text-gold mb-6 opacity-90" data-id="element-245">
              {String(point.id).padStart(2, '0')}
            </span>
            <h2 className="text-3xl md:text-4xl font-serif font-bold text-white leading-tight" data-id="element-246">
              {point.title}
            </h2>
          </div>

          <div className="space-y-6 mb-16 max-w-[700px]" data-id="element-247">
            {point.body.map((paragraph, idx) => <p key={idx} className="text-lg text-white/90 font-serif leading-relaxed" data-id="element-248">
                {paragraph}
              </p>)}
          </div>

          <div className="bg-[#252540] border border-gold/30 rounded-xl p-8 mb-12" data-id="element-249">
            <p className="text-xl font-serif font-bold text-white mb-8 text-center" data-id="element-250">
              {point.evaluationQuestion}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center" data-id="element-251">
              {point.options.map((opt, i) => {
              const isSelected = answers[currentPointIndex] === opt.value;
              return <button key={i} onClick={() => handleAnswer(opt.value)} className={`flex-1 py-4 px-6 rounded border transition-all duration-200 font-medium ${isSelected ? 'bg-gold border-gold text-navy' : 'bg-navy border-gold text-white hover:bg-[#2d2d4d]'}`} data-id="element-252">
                    {opt.text}
                  </button>;
            })}
            </div>
          </div>
        </div>

        <div className="flex justify-between items-center pt-6 border-t border-white/10" data-id="element-253">
          {currentPointIndex > 0 ? <button onClick={handlePrev} className="text-gray-light hover:text-white transition-colors px-4 py-2" data-id="element-254">
              ← Anterior
            </button> : <div data-id="element-255"></div>}
          <button onClick={handleNext} disabled={!hasAnswered} className={`font-bold px-8 py-3 rounded transition-all ${hasAnswered ? 'bg-gold text-navy hover:bg-yellow-500' : 'bg-navy-dark text-gray-light/30 cursor-not-allowed border border-white/10'}`} data-id="element-256">
            Siguiente →
          </button>
        </div>
      </motion.div>;
  };
  const renderResults = () => {
    const totalScore = answers.reduce((a, b) => a + b, 0);
    let profileKey: ProfileType = 'embrionaria';
    if (totalScore >= 6 && totalScore <= 10) profileKey = 'parcial';
    if (totalScore >= 11) profileKey = 'solida';
    const profileData = profiles[profileKey];
    let minScore = 3;
    let weakestPointIndex = 0;
    answers.forEach((score, index) => {
      if (score < minScore) {
        minScore = score;
        weakestPointIndex = index;
      }
    });
    const weakestPoint = points[weakestPointIndex];
    const weakestAnalysis = pointAnalysis[weakestPoint.id];
    const whatsappUrl = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(profileData.whatsappMsg)}`;
    return <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} className="min-h-screen py-20 px-6 max-w-3xl mx-auto" data-id="element-257">
        {/* Section A */}
        <div className="text-center mb-16" data-id="element-258">
          <h1 className="text-2xl font-serif font-bold text-white mb-8" data-id="element-259">
            Tu nivel actual de estructura operativa
          </h1>
          <h2 className="text-4xl md:text-5xl font-serif font-bold text-gold mb-6 leading-tight" data-id="element-260">
            {profileData.name}
          </h2>
          <p className="text-gray-light text-lg" data-id="element-261">{totalScore} de 16</p>
        </div>

        {/* Section B */}
        <div className="space-y-6 text-lg text-white font-serif leading-relaxed mb-20 max-w-[700px] mx-auto text-center" data-id="element-262">
          {profileData.analysis.map((p, i) => <p key={i} data-id="element-263">{p}</p>)}
        </div>

        {/* Section C */}
        <div className="mb-16 bg-navy-dark p-8 md:p-10 rounded-xl border border-white/5" data-id="element-264">
          <h3 className="text-2xl font-serif font-bold text-gold mb-6" data-id="element-265">
            Tu punto más débil: Punto {weakestPoint.id} — {weakestPoint.title}
          </h3>
          <p className="text-white font-serif text-lg leading-relaxed" data-id="element-266">
            {weakestAnalysis}
          </p>
        </div>

        {/* Section D — Download PDF */}
        <div className="text-center mb-16 print-hide" data-id="element-267">
          <button onClick={() => window.print()} className="inline-flex items-center gap-3 border border-gold/40 text-gold hover:bg-gold/10 font-medium text-lg px-8 py-4 rounded transition-all duration-200" data-id="element-268">
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-269">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" data-id="element-270" />
              <polyline points="7 10 12 15 17 10" data-id="element-271" />
              <line x1="12" y1="15" x2="12" y2="3" data-id="element-272" />
            </svg>
            Descargar mi resultado en PDF →
          </button>
        </div>

        {/* Section E — WhatsApp CTA */}
        <div className="text-center border border-gold p-8 md:p-12 rounded-xl mb-20 print-hide" data-id="element-273">
          <h2 className="text-3xl font-serif font-bold text-gold mb-4" data-id="element-274">
            ¿Quieres revisar tu caso específico?
          </h2>
          <p className="text-white font-sans text-lg mb-10 max-w-xl mx-auto" data-id="element-275">
            Te respondo personalmente. Sin formularios. Sin secuencias
            automáticas. Una conversación directa.
          </p>
          <a href={whatsappUrl} target="_blank" rel="noopener noreferrer" className="inline-block bg-gold hover:bg-yellow-500 text-navy font-bold text-xl px-10 py-5 rounded transition-all duration-300 hover:scale-105 shadow-xl w-full sm:w-auto" data-id="element-276">
            Hablemos por WhatsApp →
          </a>
          <p className="text-sm text-gray-light mt-6" data-id="element-277">
            Para dueños de negocio con operación activa.
          </p>
        </div>

        {/* Section F — Footer */}
        <div className="text-center border-t border-white/10 pt-12 pb-8 print-hide" data-id="element-278">
          <img src="https://cdn.magicpatterns.com/uploads/8iq9FdZPWhhg1moFGMfStg/Gemini_Generated_Image_vohteevohteevoht_(1)-Photoroom.png" alt="OR Logo" className="h-10 mx-auto mb-4 opacity-50" data-id="element-279" />
          <a href="https://instagram.com/oscarrodriguezm" target="_blank" rel="noopener noreferrer" className="text-gold hover:text-yellow-500 text-sm font-serif block mb-6" data-id="element-280">
            @oscarrodriguezm
          </a>
          <div className="flex justify-center gap-4 text-sm text-gray-light mb-4" data-id="element-281">
            <a href="https://instagram.com/oscarrodriguezm" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors" data-id="element-282">
              Instagram
            </a>
            <span data-id="element-283">|</span>
            <span className="hover:text-white transition-colors cursor-pointer" data-id="element-284">
              Privacidad
            </span>
          </div>
          <p className="text-sm text-gray-light" data-id="element-285">
            © 2026 Oscar Rodriguez. Todos los derechos reservados.
          </p>
        </div>
      </motion.div>;
  };
  return <div className="min-h-screen bg-navy text-white font-sans selection:bg-gold/30 selection:text-white" data-id="element-286">
      <AnimatePresence mode="wait" data-id="element-287">
        {currentScreen === 'entry' && renderEntry()}
        {currentScreen === 'points' && renderPoint()}
        {currentScreen === 'results' && renderResults()}
      </AnimatePresence>
    </div>;
}